/*******************************************************************
* PAGE        : MainLayout
* REMARKS     : Comman App Main Layout
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 13-02-2026
********************************************************************/

import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Navigate } from 'react-router-dom';
import SidebarComponent from '../components/SidebarComponent/SidebarComponent';
import HeaderComponent from '../components/HeaderComponent/HeaderComponent';
import { useSelector } from 'react-redux';
import { NotificationProvider } from '../store/NotificationContext';

const DRAWER_WIDTH = 280; // Sidebar width when expanded
const DRAWER_COLLAPSED_WIDTH = 65; // Sidebar width when collapsed

/**
 * MainLayout
 * --------------------
 * This layout component is the primary wrapper for all pages, managing:
 * - Sidebar visibility (responsive and static)
 * - Header (with sidebar toggle)
 * - Authentication redirection (if not authenticated)
 * It ensures the layout adapts for both mobile and desktop screens.
 */
const MainLayout = ({ children }) => {

    // Theme and media query hooks for responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // True for screens smaller than 900px

    // Check if the user is authenticated (redirect to login if not)
    const isAuthenticated = useSelector(state => state.reduxMaster.isAuthenticated);
    const { allowedRoutes } = useSelector(state => state.reduxMaster);
    const hasMenuAccess = allowedRoutes.length > 0;

    // State to manage sidebar (expanded/collapsed) and mobile drawer
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false); // For mobile-specific drawer

    useEffect(() => {
        if (!hasMenuAccess) {
            setSidebarOpen(true);
            setMobileOpen(true);
        }
    }, [hasMenuAccess]);

    // Redirect to login if user is not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Toggle sidebar visibility
    const handleSidebarToggle = () => {
        //  if (!hasMenuAccess) return; // block opening

        if (isMobile) {
            // Toggle mobile-specific drawer
            setMobileOpen(!mobileOpen);
        } else {
            // Toggle desktop sidebar
            setSidebarOpen((prev) => !prev);
        }
    };

    // Close mobile drawer
    const handleDrawerClose = () => {
        setSidebarOpen(false);
        setMobileOpen(false);
    };

    return (
        <NotificationProvider>
            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white' }}>
                {/* Sidebar - Responsive to mobile and desktop */}
                <SidebarComponent
                    sidebarOpen={isMobile ? mobileOpen : sidebarOpen} // Decide on sidebar state based on device size
                    toggleSidebar={handleSidebarToggle} // Toggle function for sidebar
                    onClose={handleDrawerClose} // Close mobile drawer
                    isMobile={isMobile} // Pass mobile info for responsive behavior
                />

                {/* Main Content Area */}
                <Box component="main" className='mainLayout'
                    sx={{
                        flexGrow: 1,
                        transition: 'margin 0.3s ease',
                        width: { xs: '100%', md: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH}px)` }, // Adjust width based on sidebar
                    }}
                >
                    {/* Header - Responsive with sidebar toggle */}
                    <HeaderComponent
                        toggleSidebar={handleSidebarToggle} // Function to toggle sidebar
                        sidebarIsOpen={isMobile ? mobileOpen : sidebarOpen} // Pass the current state of the sidebar
                        isMobile={isMobile} // Mobile check for responsive behavior
                    />

                    {/* Main content (children) */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, sm: 3 }, padding: '0 !important', backgroundColor: '#FFFFFF' }}>
                        <div className="app-main-children page-content">{children}</div> {/* Render child components */}
                    </Box>
                </Box>
            </Box>
        </NotificationProvider>
    );
};

export default MainLayout;



//  <Box sx={{ display: 'flex' }}>
{/* <div className="app-main-children">{children}</div> */ }
//             {/* Sidebar */}
//             <SidebarComponent open={sidebarOpen} toggleSidebar={handleSidebarToggle} />

//             {/* Main Content Area */}
//             <Box
//                 sx={{
//                     flexGrow: 1,
//                     padding: '20px',
//                     transition: 'margin-left 0.3s', // Smooth transition when the sidebar opens/closes
//                     marginLeft: sidebarOpen ? '250px' : '0', // Adjust main content based on sidebar state
//                 }}
//             >
//                 <HeaderComponent toggleSidebar={handleSidebarToggle} />
//                 <div className='app-main-children'>{children}</div>
//             </Box>
//         </Box>